import {
  type QueryClient,
  QueryErrorResetBoundary,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  retainSearchParams,
  useRouteContext,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { Page, ToolbarSlot } from "@/client/components/page";
import {
  Delayed,
  HeldButton,
  PagePending,
} from "@/client/components/page-pending";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import {
  type CompanySession,
  ensureTenantData,
  requireModule,
} from "@/client/lib/session";
import { dayIn, monthIn } from "@/features/attendance/rules";
import { MonthStepper } from "@/features/payslips/components/month-stepper";
import {
  MyPayslips,
  MyPayslipsSkeleton,
} from "@/features/payslips/components/my-payslips";
import {
  MonthHeader,
  PayslipMonth,
} from "@/features/payslips/components/payslip-month";
import { PayslipTableSkeleton } from "@/features/payslips/components/payslip-table";
import { PayslipsHeading } from "@/features/payslips/components/payslips-heading";
import { payslipsCopy } from "@/features/payslips/copy";
import { payslipsSearch } from "@/features/payslips/month-search";
import { monthAfter } from "@/features/payslips/months";
import {
  monthSlipsQuery,
  mySlipsQuery,
  openMonth,
} from "@/features/payslips/payslips-query";
import type { PayslipMonth as MonthSlips } from "@/features/payslips/schemas";
import { peopleQuery } from "@/features/people/people-query";

/**
 * An Admin opens on last month while it has slips to publish, and on this month once they are
 * all published or there was nobody to pay. Both are opened at once, so the redirect lands on a
 * month already held instead of drafting it in a second round trip. This month failing does not
 * stop last month opening; opened on this month, the redirected load opens it again and says why.
 */
async function openingMonth(
  queryClient: QueryClient,
  session: CompanySession,
  current: string,
) {
  const last = monthAfter(current, -1);
  const [lastMonth] = await Promise.allSettled([
    openMonth(queryClient, session, last),
    openMonth(queryClient, session, current),
  ]);
  if (lastMonth.status === "rejected") throw lastMonth.reason;
  return lastMonth.value.items.some((slip) => slip.status === "draft")
    ? last
    : current;
}

export const Route = createFileRoute("/_authenticated/$slug/payslips/")({
  validateSearch: payslipsSearch,
  // A month in the address stays when the sidebar link or a slip's page comes back here.
  search: { middlewares: [retainSearchParams(["payMonth"])] },
  loaderDeps: ({ search }) => ({ month: search.payMonth }),
  beforeLoad: ({ context }) => requireModule(context, "payslips"),
  // Opening a month drafts it, a write with an audit row, so hovering a link must not.
  preload: false,
  // The month an Admin sees; null for anybody else, who sees their own slips.
  loader: async ({ context, deps, params, cause }) => {
    if (context.role !== "admin") {
      await ensureTenantData(context.queryClient, mySlipsQuery);
      return null;
    }
    const current = monthIn(context.companyTimezone, new Date());
    // The default goes into the address, so publishing last month does not move the page on
    // the next reload of this match.
    if (!deps.month || deps.month > current) {
      const opening = openingMonth(context.queryClient, context, current);
      // Read beside the opening month, so the redirected load finds them held or on their way.
      void context.queryClient.prefetchQuery(peopleQuery);
      void context.queryClient.prefetchQuery(mySlipsQuery);
      throw redirect({
        to: "/$slug/payslips",
        params,
        search: { payMonth: await opening },
        replace: true,
      });
    }
    // A step comes from a month on screen, whose first month is held: an earlier month would be
    // drafted, so it is never opened.
    const first = cause === "stay" ? heldFirstMonth(context.queryClient) : "";
    if (deps.month < first) {
      throw redirect({
        to: "/$slug/payslips",
        params,
        search: { payMonth: first },
        replace: true,
      });
    }
    const slips = monthSlipsQuery(deps.month).queryKey;
    // A month held from an earlier visit and invalidated since would draw its old slips while it
    // drafts, and go on drawing them if the draft were refused. Dropped, it waits on the draft.
    if (
      cause === "stay" &&
      context.queryClient.getQueryState(slips)?.isInvalidated &&
      !context.queryClient
        .getQueryCache()
        .find({ queryKey: slips, exact: true })
        ?.getObserversCount()
    ) {
      context.queryClient.removeQueries({ queryKey: slips, exact: true });
    }
    const reads = Promise.all([
      openMonth(context.queryClient, context, deps.month),
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, mySlipsQuery),
    ]);
    // A step from the month on screen is not awaited: the title and toolbar stay mounted while the
    // month loads under them, and a failed read is refused there.
    if (cause === "stay") {
      void reads.catch(() => undefined);
      return { month: deps.month, current };
    }
    const [{ firstMonth }] = await reads;
    if (deps.month < firstMonth) {
      throw redirect({
        to: "/$slug/payslips",
        params,
        search: { payMonth: firstMonth },
        replace: true,
      });
    }
    return { month: deps.month, current };
  },
  head: async ({ match }) => {
    const { payslipsCopy } = await import("@/features/payslips/copy");
    return {
      meta: [
        { title: pageTitle(payslipsCopy.title, match.context.companyName) },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: PayslipsPending,
  component: Payslips,
});

/**
 * An Admin's month in the Table shape; the month in the address steps for real, and Publish waits
 * on the month's slips. Anybody else's own slips are a list.
 */
function PayslipsPending() {
  const { slug } = Route.useParams();
  const { role, companyTimezone } = useRouteContext({
    from: "/_authenticated/$slug",
  });
  const queryClient = useQueryClient();
  const asked = Route.useSearch().payMonth;
  const current = monthIn(companyTimezone, new Date());
  // A month after this one is redirected away, so it is not the title.
  const payMonth = asked && asked <= current ? asked : undefined;
  if (role !== "admin") {
    return (
      <PagePending
        shape="list"
        className="min-h-full"
        header={<PayslipsHeading />}
      >
        <MyPayslipsSkeleton />
      </PagePending>
    );
  }
  // Until a month is read the company's first is not known, so Previous stays open.
  const firstMonth =
    (payMonth &&
      queryClient.getQueryData(monthSlipsQuery(payMonth).queryKey)
        ?.firstMonth) ||
    "";
  return (
    <PagePending
      shape="table"
      className="min-h-full"
      header={
        <MonthHeader
          stepper={
            payMonth ? (
              <MonthStepper
                slug={slug}
                month={payMonth}
                firstMonth={firstMonth}
                current={current}
              />
            ) : null
          }
          actions={<HeldButton>{payslipsCopy.month.publish(0)}</HeldButton>}
        />
      }
    >
      <PayslipTableSkeleton />
    </PagePending>
  );
}

function Payslips() {
  const { slug } = Route.useParams();
  const { companyTimezone } = Route.useRouteContext();
  const shown = Route.useLoaderData();
  return shown ? (
    <AdminMonth
      slug={slug}
      month={shown.month}
      current={shown.current}
      zone={companyTimezone}
    />
  ) : (
    <Page shape="list" className="min-h-full">
      <PayslipsHeading />
      <Mine slug={slug} zone={companyTimezone} />
    </Page>
  );
}

/** The company's first month, from any month already read: it is the same for every month. */
function heldFirstMonth(queryClient: QueryClient) {
  return (
    queryClient
      .getQueriesData<MonthSlips | unknown[]>({ queryKey: ["pay", "payslips"] })
      .map(([, held]) => held)
      .find((held): held is MonthSlips => !!held && "firstMonth" in held)
      ?.firstMonth ?? ""
  );
}

/**
 * An Admin's month. The title, stepper and actions sit outside the month's Suspense and read the
 * month from the address, so a step loads the next month under a stepper that keeps focus.
 */
function AdminMonth({
  slug,
  month,
  current,
  zone,
}: {
  slug: string;
  month: string;
  current: string;
  zone: string;
}) {
  const queryClient = useQueryClient();
  // Only what is held, never a read: the loader opens a month, which drafts it first.
  const { data } = useQuery({ ...monthSlipsQuery(month), enabled: false });
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <Page shape="table" className="min-h-full">
      <MonthHeader
        slot={setSlot}
        stepper={
          <MonthStepper
            slug={slug}
            month={month}
            firstMonth={data?.firstMonth ?? heldFirstMonth(queryClient)}
            current={current}
          />
        }
      />
      <ToolbarSlot value={slot}>
        {/* A boundary of its own, never reset, so a month that failed is not read again on its
            own when the page comes back: only the loader's draft refills it. */}
        <QueryErrorResetBoundary>
          <Suspense
            fallback={
              <Delayed>
                <PayslipTableSkeleton />
              </Delayed>
            }
          >
            <Month slug={slug} month={month} zone={zone} />
          </Suspense>
        </QueryErrorResetBoundary>
      </ToolbarSlot>
    </Page>
  );
}

function Month({
  slug,
  month,
  zone,
}: {
  slug: string;
  month: string;
  zone: string;
}) {
  const { data } = useSuspenseQuery(monthSlipsQuery(month));
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: mine } = useSuspenseQuery(mySlipsQuery);
  const faces = new Map(
    people.map((row) => [
      row.id,
      { photoKey: row.photoKey, jobTitle: row.jobTitle },
    ]),
  );
  return (
    <PayslipMonth
      key={month}
      slug={slug}
      month={month}
      today={dayIn(zone, new Date())}
      timeZone={zone}
      data={data}
      faces={faces}
      ownSlips={mine.length > 0}
    />
  );
}

function Mine({ slug, zone }: { slug: string; zone: string }) {
  const { data } = useSuspenseQuery(mySlipsQuery);
  return <MyPayslips slug={slug} slips={data} timeZone={zone} />;
}
